// What the halls lay along their walls and stand in their water (docs/166 C2): the crypts' strips at a wall's
// foot, the ashlands sewer's lead pipes along a front, the glint of a torch in the water below it, and the
// stands a kit made for water or lava, put on the pillars the forge stood there.
import type { Piece, Standing } from '../types';
import { Canvas, type RGBA } from './canvas';
import { C, type Ctx } from './context';
import { isLiquid, type Ground } from './ground';

type Grounds = ReadonlyArray<Ground | undefined>;

// ---- which front carries which dressing: the same rolls as `paintWalls`, so the halls know what it hung --------------

/** A front square of a built kit's wall (drawn, with no drawn wall below it, facing an open square). */
export function frontAt(ctx: Ctx, x: number, y: number): boolean {
  const { sq, look } = ctx;
  const wallAt = (a: number, b: number): boolean => sq.isWall(a, b);
  const pillar = (a: number, b: number): boolean => ctx.has('stand') && sq.at(a, b) === 'wall' && !wallAt(a - 1, b) && !wallAt(a + 1, b) && !wallAt(a, b - 1) && !wallAt(a, b + 1);
  const rock = (a: number, b: number): boolean => (look.rock_is_top ? false : sq.at(a, b) === 'rock');
  const doorAt = (a: number, b: number): boolean => { const s = sq.at(a, b); return (s === 'door' || s === 'way') && !(sq.solid(a, b - 1) && sq.solid(a, b + 1)); };
  const drawn = (a: number, b: number): boolean => (wallAt(a, b) && !rock(a, b) && !pillar(a, b)) || doorAt(a, b);
  if (!drawn(x, y) || doorAt(x, y) || drawn(x, y + 1)) return false;
  return sq.on(x, y + 1) && !sq.solid(x, y + 1);
}

/** The dressing `paintWalls` hung on the front at (x, y), if any (look.json `wall_dress`, its rolls). */
export function dressAt(ctx: Ctx, x: number, y: number): string | undefined {
  if (!frontAt(ctx, x, y)) return undefined;
  const dress = (ctx.look.wall_dress ?? ['wall_lit', 'wall_face_banner', 'wall_face_shield', 'wall_face_alcove']).filter((p) => ctx.has(p));
  if (!dress.length) return undefined;
  const r0 = ctx.roll('dress', x, y) % (ctx.look.wall_dress_every ?? 9);
  if (r0 >= dress.length || (r0 === 0 && ctx.roll('torch', x, y) % 2)) return undefined;
  return dress[r0];
}

// ---- strips at a wall's foot (`frost_foot`, `silt_foot`, `dust_foot`): on the floor square below a wall, turned for one beside it ----

const FEET = ['frost_foot', 'silt_foot', 'dust_foot'];

/** Laid after the wall foot's shadow (docs/166 C2, the desert crypt), or the shadow takes it to mud. */
export function wallFeet(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  const piece = FEET.find((p) => ctx.has(p));
  if (!piece) return;
  const { sq } = ctx, { w, h } = sq;
  const wall = (x: number, y: number): boolean => !sq.on(x, y) || sq.isWall(x, y);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.at(x, y), g = grounds[y * w + x];
    if (s !== 'floor' && s !== 'rough') continue;
    if (!g || isLiquid(g.name)) continue;
    const X = x * C, Y = y * C;
    if (wall(x, y - 1)) out.draw(ctx.pick(piece, 'fn', x, y)!, X, Y);
    // Pillow's ROTATE_90 (counter-clockwise) for a wall on the west, ROTATE_270 (clockwise) on the east
    if (wall(x - 1, y)) out.draw(ctx.pick(piece, 'fw', x, y)!, X, Y, { rot90: true, flipX: true, flipY: true });
    if (wall(x + 1, y)) out.draw(ctx.pick(piece, 'fe', x, y)!, X, Y, { rot90: true });
  }
}

// ---- the ashlands sewer's pipe run (look.json `pipe_run`) ----------------------------------------------------------

/**
 * Lead pipes along a wall: `wall_face_pipehead`, any number of `wall_face_piperun`, `wall_face_pipetail`, on
 * consecutive plain fronts at `wall_dressing_y`. A few runs a board, each on a run of undressed fronts.
 */
export function pipeRuns(ctx: Ctx, standing: Standing[]): void {
  if (!ctx.look['pipe_run'] || !ctx.has('wall_face_pipehead') || !ctx.has('wall_face_pipetail')) return;
  const { sq } = ctx, { w, h } = sq;
  const dy = ctx.look.wall_dressing_y ?? 0;
  const foot = new Map<number, Standing>();
  for (const s of standing) if (s.kind === 'wall') foot.set(s.at.y * w + s.at.x, s);
  for (let y = 0; y < h; y++) {
    let x = 0;
    while (x < w) {
      const plain = (a: number): boolean => frontAt(ctx, a, y) && !dressAt(ctx, a, y) && foot.has(y * w + a);
      if (!plain(x)) { x++; continue; }
      let e = x;
      while (e + 1 < w && plain(e + 1)) e++;
      const len = e - x + 1;
      // one run in about five long stretches, 3 to 6 fronts long, starting a seeded way along
      if (len >= 4 && ctx.roll('pipe run', x, y) % 5 === 0) {
        const n = Math.min(len - 1, 3 + (ctx.roll('pipe len', x, y) % 4)), x0 = x + (ctx.roll('pipe at', x, y) % (len - n + 1));
        for (let k = 0; k < n; k++) {
          const name = k === 0 ? 'wall_face_pipehead' : k === n - 1 ? 'wall_face_pipetail' : 'wall_face_piperun';
          const p = ctx.pick(name, 'pr', x0 + k, y)!, wall = foot.get(y * w + x0 + k)!;
          standing.push({ kind: 'piece', at: { x: x0 + k, y }, x: (x0 + k) * C, y: y * C + dy, w: p.w, h: p.h, foot: wall.foot + 0.01, frame: p.frame });
        }
      }
      x = e + 1;
    }
  }
}

// ---- a torch's glint on the water below it (the cisterns) ----------------------------------------------------------

export function torchGlints(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  if (!ctx.has('torch_glint')) return;
  const { sq } = ctx, { w, h } = sq;
  for (let y = 0; y < h - 1; y++) for (let x = 0; x < w; x++) {
    const g = grounds[(y + 1) * w + x];
    if (!g || g.name !== 'water' && g.name !== 'deep') continue;
    if (dressAt(ctx, x, y) !== 'wall_lit') continue;
    out.draw(ctx.pick('torch_glint', 'gl', x, y)!, x * C, (y + 1) * C + 7);
  }
}

// ---- the stands a kit made for one place: dry pillars, pillars in water or lava, rare ones ------------------------

/** Per kit and biome, which `stand` files (by number) go on dry floor and which in a pool, and how often each. */
interface StandRule { dry: Record<number, number>; wet?: Record<number, number> }
const STANDS: Record<string, StandRule> = {
  // the cisterns: `stand_01` the pier on the flags; the rest are made for water (the tide gauge, the noria, a vishap…)
  'cistern/boreal': { dry: { 1: 1 }, wet: { 2: 3, 3: 3, 4: 1 } },
  'cistern/desert': { dry: { 1: 1 }, wet: { 2: 3, 3: 3, 4: 1 } },
  'cistern/highland': { dry: { 1: 1 }, wet: { 2: 3, 3: 3, 4: 1 } },
  'cistern/wetland': { dry: { 1: 1 }, wet: { 2: 3, 3: 3, 4: 1 } },
  'cistern/ashlands': { dry: { 1: 1 }, wet: { 2: 3, 3: 2, 4: 2, 5: 3 } },
  'cistern/underdark': { dry: { 1: 1 }, wet: { 2: 3, 3: 1, 4: 2 } },
  'cistern/jungle': { dry: { 1: 4, 4: 1 }, wet: { 2: 3, 3: 1 } },
  // the embers' columns and trunks made to stand in lava (docs/166 C2)
  'ember/wetland': { dry: { 1: 1, 2: 1, 3: 1, 4: 1 }, wet: { 5: 1, 6: 1 } },
  'ember/desert': { dry: { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 }, wet: { 6: 1 } },
  'ember/highland': { dry: { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 }, wet: { 6: 1 } },
  'ember/jungle': { dry: { 1: 1, 2: 1, 3: 1, 4: 1, 6: 1 }, wet: { 5: 1 } },
  'ember/underdark': { dry: { 1: 1, 2: 1, 3: 1, 4: 1, 6: 1, 7: 1 }, wet: { 5: 1 } },
  'ember/ashlands': { dry: { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 }, wet: { 6: 1, 7: 1 } },
  // rare charms the notes ask a forge to keep rare: the ashlands tomb's great jar, the underdark cavern's gypsum
  'tomb/ashlands': { dry: { 1: 4, 2: 4, 3: 1 } },
  'cavern/underdark': { dry: { 1: 3, 2: 3, 3: 3, 4: 3, 5: 1 } },
};

const pickBy = (ctx: Ctx, table: Record<number, number>, vs: readonly Piece[], x: number, y: number): Piece | undefined => {
  const opts = vs.filter((p) => (table[p.v] ?? 0) > 0);
  if (!opts.length) return undefined;
  let total = 0;
  for (const p of opts) total += table[p.v]!;
  let t = (ctx.roll('stand rule', x, y) >>> 7) % total;
  for (const p of opts) { t -= table[p.v]!; if (t < 0) return p; }
  return opts[0];
};

/**
 * A lone wall square (a pillar) takes a stand made for where it stands: in a pool (two or more of its sides
 * liquid) a wet one, with its faint reflection and a tide ring where it meets the water; on the flags a dry one.
 * The square is a wall either way, so nothing that blocks is drawn where a figure may walk.
 */
export function restand(ctx: Ctx, out: Canvas, grounds: Grounds, standing: Standing[]): void {
  const rule = STANDS[`${ctx.set.kit}/${ctx.set.biome}`];
  if (!rule) return;
  const { sq } = ctx, { w } = sq;
  const vs = ctx.all('stand');
  const liquidAt = (x: number, y: number): string | undefined => { const g = sq.on(x, y) ? grounds[y * w + x] : undefined; return g && isLiquid(g.name) ? g.name : undefined; };
  for (const s of standing) {
    if (s.kind !== 'stand' || !s.frame?.startsWith('stand_')) continue;
    const { x, y } = s.at;
    const wet = [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(([dx, dy]) => liquidAt(x + dx!, y + dy!)).length >= 2;
    const p = (wet && rule.wet ? pickBy(ctx, rule.wet, vs, x, y) : undefined) ?? pickBy(ctx, rule.dry, vs, x, y);
    if (!p) continue;
    s.frame = p.frame; s.w = p.w; s.h = p.h; s.x = x * C + C / 2 - p.w / 2; s.y = (y + 1) * C - p.h;
    if (wet && rule.wet && (rule.wet[p.v] ?? 0) > 0 && ctx.set.kit === 'cistern') reflectAndRing(ctx, out, grounds, p, s);
  }
}

/** A stand in the water shows faint below it, and a ring where it meets the water (`reflect` in `cistern_board.py.txt`). */
function reflectAndRing(ctx: Ctx, out: Canvas, grounds: Grounds, p: Piece, s: Standing): void {
  const { w, h } = ctx.sq;
  const wetPx = (X: number, Y: number): boolean => {
    const x = Math.floor(X / C), y = Math.min(h - 1, Math.floor(Y / C));
    const g = ctx.sq.on(x, y) ? grounds[y * w + x] : undefined;
    return !!g && isLiquid(g.name);
  };
  let bottom = 0, x0 = p.w, x1 = 0;
  for (let v = 0; v < p.h; v++) for (let u = 0; u < p.w; u++) if (p.px[(v * p.w + u) * 4 + 3]) { bottom = v + 1; x0 = Math.min(x0, u); x1 = Math.max(x1, u + 1); }
  const tint: RGBA = ctx.set.biome === 'boreal' ? [10, 38, 46, 255] : [10, 30, 40, 255];
  const yb = s.y + bottom;
  for (let j = 0; j < Math.min(bottom, 22); j++) for (let i = 0; i < p.w; i++) {
    if (!p.px[((bottom - 1 - j) * p.w + i) * 4 + 3]) continue;
    const X = s.x + i, Y = yb + j;
    if (!out.inside(X, Y) || !wetPx(X, Y)) continue;
    const k = (Y * out.w + X) * 4, a = (150 - j * 6) / 255;
    for (let c = 0; c < 3; c++) out.px[k + c] = out.px[k + c]! + (tint[c]! - out.px[k + c]!) * a;
  }
  // the ring: an ellipse's outline round the foot, pale and thin
  const cx = s.x + p.w / 2, cy = yb - 2 + 1, rw = (x1 - x0) / 2 + 2;
  const ring: RGBA = ctx.set.biome === 'boreal' ? [120, 190, 190, 150] : [120, 176, 186, 120];
  for (let Y = Math.floor(cy - 4); Y <= cy + 4; Y++) for (let X = Math.floor(cx - rw - 1); X <= cx + rw + 1; X++) {
    const d = ((X + 0.5 - cx) / rw) ** 2 + ((Y + 0.5 - cy) / 3.5) ** 2;
    if (d <= 1 && d > 0.6 && Y > yb - 3) out.over(X, Y, ring);
  }
}

export { Canvas };
